<template>
  <EquipmentBase :section-id="ScenarioDetailSectionKey.OPERATING_PARAMETERS">
    <EquipmentHeader>
      <template #title>Operating Parameters</template>
    </EquipmentHeader>
    <!-- Top Tubing -->
    <TubingRow
      v-if="isEspOnShore"
      :tubing="topTubing"
      :allowed-tubing-types="[Tubings_Type_Enum.Normal, Tubings_Type_Enum.Both]"
      :hide-add-above="true"
      :hide-add-below="topTubingHideAddBelowButton"
      :hide-remove="!topTubing?.id"
      @add-below="newBottomTubing = true"
      @remove="removeTubing('top')"
    />
    <!-- Bottom Tubing -->
    <TubingRow
      v-if="isEspOnShore && (newBottomTubing || bottomTubing?.id)"
      :tubing="bottomTubing"
      :top-tubing="false"
      :allowed-tubing-types="[Tubings_Type_Enum.Normal, Tubings_Type_Enum.Both]"
      :hide-add-above="true"
      :hide-add-below="true"
      :hide-remove="false"
      @remove="removeTubing('bottom')"
    />

    <Divider />

    <div class="grid justify-content-between align-items-center mt-1">
      <ConverterInput
        v-if="isEspOnShore"
        :rule-names="['target_flow_warning_alert']"
        class="col"
        label="Target Liquid Rate"
        :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
        :model="LiquidFlowRateUnits.options()"
        mode="write"
        :model-value="parameters.target_liquid_rate_std_barrel_day"
        :decimal-places="6"
        @update:model-value="
          parametersForm.update({
            target_liquid_rate_std_barrel_day: $event
          })
        "
      />
      <ConverterInput
        v-if="
          wellProperties.well_test_liquid_input_type !==
          Well_Test_Liquid_Input_Options_Enum.ProductivityIndexWaterCut
        "
        :key="`${liquidRateUnit.label}-input-liquid-rate`"
        :class="['col', { 'col-fixed': isEspOnShore }]"
        label="Input Liquid Rate"
        :model-value="wellTestFlowRates.liquidFlowRateSTBD"
        :base-unit="LiquidFlowRateUnits.BarrelsPerDay"
        :target-unit="liquidRateUnit"
        :model="LiquidFlowRateUnits.options()"
        mode="read"
      />
      <ConverterInput
        v-else
        :key="`${liquidRateUnit.label}-input-pi`"
        class="col col-fixed"
        label="Input PI"
        :model-value="productivityIndexStbdPsia"
        :base-unit="ProductivityIndexUnits.BarrelDayPsi"
        :target-unit="liquidRateUnit"
        :model="ProductivityIndexUnits.options()"
        :decimal-places="7"
        mode="read"
      />
      <ConverterInput
        v-if="isEspOnShore"
        class="col"
        label="Tubing Pressure"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :model="PressureUnits.options()"
        mode="write"
        :model-value="parameters.bottom_tubing_pressure_psia"
        @update:model-value="
          parametersForm.update({
            bottom_tubing_pressure_psia: $event
          })
        "
      />
      <ConverterInput
        v-if="isEspOnShore"
        class="col"
        label="Casing Pressure"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :model="PressureUnits.options()"
        mode="write"
        :model-value="parameters.bottom_casing_pressure_psia"
        @update:model-value="
          parametersForm.update({
            bottom_casing_pressure_psia: $event
          })
        "
      />
    </div>

    <Divider v-if="isEspOnShore" />

    <!-- Y Blocks -->
    <div v-if="isEspOnShore" class="y-tools-section">
      <div class="y-tools-header body-2 mb-2">Y Tool</div>
      <div class="y-tools-container">
        <!-- Upper Y Block -->
        <YToolRow :is-upper="true" />
        <!-- TODO: Comment this back in. Hiding the lower y tool should be sufficient to hide the rest, for now we are demoing it and don't want lower or equipment stack to show -->
        <!-- <Divider v-if="useUpperYTool && upperYToolSelected" /> -->
        <!-- Lower Y Block -->
        <!-- <YToolRow
          v-if="useUpperYTool && upperYToolSelected"
          :is-upper="false"
        /> -->
      </div>
    </div>
    <Divider />
    <!-- TODO: Comment this back in. Hiding the lower y tool should be sufficient to hide the rest, for now we are demoing it and don't want lower or equipment stack to show -->
    <!-- Add Lower Stack Equipment Toggle -->
    <!-- <div v-if="shouldShowLowerStackToggle" class="build-lower-stack-toggle">
      <LabelledToggle
        :model-value="hasLowerEquipStack"
        text="Add Lower Stack Equipment"
        @update:model-value="handleLowerStackToggle($event)"
      />
    </div> -->
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { ProductivityIndexUnits } from "@/libs/units/productivityIndex";
import { PressureUnits } from "@/libs/units/pressure";
import type { Tubings } from "gql/api";
import {
  Sizing_Pumping_Type_Enum,
  Tubings_Type_Enum,
  Well_Test_Liquid_Input_Options_Enum
} from "gql/api";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import TubingRow from "@/components/equipment/tubing/TubingRow.vue";
import YToolRow from "@/components/equipment/y_tool/YToolRow.vue";
// import LabelledToggle from "@/components/LabelledToggle.vue";
import { injectStrict } from "@/utils/utils";
import {
  parametersFormKey,
  scenarioCalculationsKey,
  wellPropertiesFormKey,
  equipmentFormKey,
  scenarioKey,
  sizingKey,
  sizingFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import type { ConvertableUnit } from "@/libs/units";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";

const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const wellProperties = computed(() => wellPropertiesForm.data.value);
const parametersForm = injectStrict(parametersFormKey);
const parameters = computed(() => parametersForm.data.value);
const equipmentForm = injectStrict(equipmentFormKey);
const equipment = computed(() => equipmentForm.data.value);

const { wellTestFlowRates, productivityIndexStbdPsia } = injectStrict(
  scenarioCalculationsKey
);

const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const sizingForm = injectStrict(sizingFormKey, null);

const { getLiquidFlowUnit, getProductivityFlowUnit } = useScenarioUnits(
  scenario,
  sizing
);

// Unit for total Liquid Rate should always reflect Target Liquid Rate
const liquidRateUnit = computed<ConvertableUnit>(() =>
  wellProperties.value.well_test_liquid_input_type ===
  Well_Test_Liquid_Input_Options_Enum.ProductivityIndexWaterCut
    ? getProductivityFlowUnit("Productivity Index.convert.stb/d/psi").value
    : getLiquidFlowUnit("Total Liquid Rate.write.STB/D").value
);

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const topTubing = computed(() => wellProperties.value.r_top_tubing as Tubings);
const bottomTubing = computed(
  () => wellProperties.value.r_bottom_tubing as Tubings
);
const topTubingHideAddBelowButton = computed(() => {
  return (
    newBottomTubing.value || !!bottomTubing.value?.id || !topTubing.value?.id
  );
});

const newBottomTubing = ref(false);

// const hasLowerEquipStack = computed(() =>
//   Boolean(equipment.value.has_lower_equip_stack)
// );

const useUpperYTool = computed(() =>
  Boolean(equipment.value.use_uphole_y_tool)
);

const upperYToolSelected = computed(() =>
  Boolean(equipment.value.r_uphole_y_tool)
);

const useLowerYTool = computed(() =>
  Boolean(equipment.value.use_downhole_y_tool)
);

const lowerYToolSelected = computed(() =>
  Boolean(equipment.value.r_downhole_y_tool)
);

// const shouldShowLowerStackToggle = computed(() => {
//   return (
//     isEspOnShore.value &&
//     useUpperYTool.value &&
//     upperYToolSelected.value &&
//     useLowerYTool.value &&
//     lowerYToolSelected.value
//   );
// });

// function handleLowerStackToggle(enabled: boolean) {
//   equipmentForm.update({
//     has_lower_equip_stack: enabled
//   });
// }

/**
 * Cleanup function: When upper Y tool is disabled or deselected,
 * automatically disable dependent lower Y tool and lower stack options.
 */
function cleanupLowerYToolAndStack() {
  equipmentForm.update({
    use_downhole_y_tool: false,
    has_lower_equip_stack: false
  });
}

/**
 * Cleanup function: When lower Y tool is disabled or deselected,
 * automatically disable dependent lower stack option.
 */
function cleanupLowerStack() {
  equipmentForm.update({
    has_lower_equip_stack: false
  });
}

// Watch for upper Y tool toggle changes - cleanup dependent options when disabled
watch(useUpperYTool, (isEnabled, wasEnabled) => {
  if (wasEnabled && !isEnabled) {
    cleanupLowerYToolAndStack();
  }
});

// Watch for upper Y tool selection changes - cleanup dependent options when deselected
watch(upperYToolSelected, (isSelected, wasSelected) => {
  if (wasSelected && !isSelected) {
    cleanupLowerYToolAndStack();
  }
});

// Watch for lower Y tool toggle changes - cleanup dependent options when disabled
watch(useLowerYTool, (isEnabled, wasEnabled) => {
  if (wasEnabled && !isEnabled) {
    cleanupLowerStack();
  }
});

// Watch for lower Y tool selection changes - cleanup dependent options when deselected
watch(lowerYToolSelected, (isSelected, wasSelected) => {
  if (wasSelected && !isSelected) {
    cleanupLowerStack();
  }
});

function removeTubing(tubingPosition: "top" | "bottom") {
  // save bottom tubing data
  const { r_bottom_tubing, bottom_tubing_length_feet, bottom_tubing_id } =
    wellProperties.value;

  if (tubingPosition === "bottom") {
    // clear bottom tubing
    wellPropertiesForm.update({
      r_bottom_tubing: null,
      bottom_tubing_id: null,
      bottom_tubing_length_feet: null
    });
  }
  // if top tubing was deleted
  // move bottom tubing data to top tubing
  if (tubingPosition === "top") {
    wellPropertiesForm.update({
      r_top_tubing: r_bottom_tubing,
      top_tubing_id: bottom_tubing_id,
      top_tubing_length_feet: bottom_tubing_length_feet
    });
    wellPropertiesForm.update({
      r_bottom_tubing: null,
      bottom_tubing_id: null,
      bottom_tubing_length_feet: null
    });
  }
  newBottomTubing.value = false;
}
</script>

<style scoped>
.y-tools-section {
  margin-top: 1rem;
}

.y-tools-header {
  color: var(--gray-700);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.y-tools-container {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.build-lower-stack-toggle {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}
</style>
